import { useEffect, useState } from 'react';
import { establishmentAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Loader2, Save, Banknote, Clock, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { id: '0', name: 'Lundi' },
  { id: '1', name: 'Mardi' },
  { id: '2', name: 'Mercredi' },
  { id: '3', name: 'Jeudi' },
  { id: '4', name: 'Vendredi' },
  { id: '5', name: 'Samedi' },
  { id: '6', name: 'Dimanche' }
];

const DEFAULT_SCHEDULE = DAYS.reduce((acc, d) => {
  acc[d.id] = { open: true, start: '10:00', end: '23:00' };
  return acc;
}, {});

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    address: '',
    google_review_url: '',
    accepts_cash_payment: true,
    manual_order_completion: false,
    opening_hours: { enabled: false, schedule: { ...DEFAULT_SCHEDULE } },

    hubrise_enabled: false,
    hubrise_auto_send_orders: false,
    hubrise_location_id: '',
    hubrise_access_token: '',
    hubrise_connection_name: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await establishmentAPI.get();
        const d = response.data;
        setForm({
          name: d.name || '',
          description: d.description || '',
          address: d.address || '',
          google_review_url: d.google_review_url || '',
          accepts_cash_payment: d.accepts_cash_payment !== false,
          manual_order_completion: d.manual_order_completion === true,
          opening_hours: d.opening_hours || { enabled: false, schedule: { ...DEFAULT_SCHEDULE } },

          hubrise_enabled: d.hubrise_enabled === true,
          hubrise_auto_send_orders: d.hubrise_auto_send_orders === true,
          hubrise_location_id: d.hubrise_location_id || '',
          hubrise_access_token: d.hubrise_access_token || '',
          hubrise_connection_name: d.hubrise_connection_name || ''
        });
      } catch (err) {
        console.error('Error loading establishment:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await establishmentAPI.update(form);
      toast.success('Parametres enregistres');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSchedule = (dayId, field, value) => {
    setForm(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        schedule: {
          ...prev.opening_hours.schedule,
          [dayId]: {
            ...(prev.opening_hours.schedule?.[dayId] || { open: true, start: '10:00', end: '23:00' }),
            [field]: value
          }
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const schedule = form.opening_hours?.schedule || DEFAULT_SCHEDULE;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
          Parametres
        </h1>
        <p className="text-gray-500">Configurez votre etablissement</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader><CardTitle>Avis Google après la commande</CardTitle><CardDescription>Après le pourboire, tous les clients peuvent partager leur expérience, même sans laisser de pourboire.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <Label htmlFor="google-review-url">Lien pour laisser un avis Google</Label>
            <Input id="google-review-url" type="url" placeholder="https://g.page/r/…/review" value={form.google_review_url} onChange={e => setForm({ ...form, google_review_url: e.target.value })} />
            <p className="text-sm text-slate-600">Dans votre fiche Google Business Profile, choisissez « Demander des avis » et copiez le lien. Laissez ce champ vide pour désactiver le bouton.</p>
            {/^(https:\/\/)(g\.page|maps\.app\.goo\.gl|search\.google\.com|(?:www\.|maps\.)?google\.(?:com|fr))\//.test(form.google_review_url) && <a className="inline-flex min-h-11 items-center text-violet-700 underline" href={form.google_review_url} target="_blank" rel="noopener noreferrer">Tester le formulaire Google ↗</a>}
          </CardContent>
        </Card>
        {/* General Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Nom</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="settings-name-input" />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" data-testid="settings-description-input" />
            </div>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input id="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="settings-address-input" />
            </div>
          </CardContent>
        </Card>

        {/* Payments */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <Banknote className="w-5 h-5" />Paiements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Paiement en especes</p>
                <p className="text-sm text-gray-500">
                  {form.accepts_cash_payment ? 'Les clients peuvent payer au comptoir' : 'Paiement par carte uniquement'}
                </p>
              </div>
              <Switch checked={form.accepts_cash_payment} onCheckedChange={(checked) => setForm({ ...form, accepts_cash_payment: checked })} data-testid="cash-payment-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Opening Hours */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Clock className="w-5 h-5" />Horaires d'ouverture
              </CardTitle>
              <Switch
                checked={form.opening_hours?.enabled || false}
                onCheckedChange={(checked) => setForm(prev => ({
                  ...prev,
                  opening_hours: {
                    ...prev.opening_hours,
                    enabled: checked,
                    schedule: prev.opening_hours?.schedule || { ...DEFAULT_SCHEDULE }
                  }
                }))}
                data-testid="opening-hours-enabled-switch"
              />
            </div>
            <CardDescription>
              {form.opening_hours?.enabled
                ? 'Les commandes ne seront possibles que pendant les heures d\'ouverture'
                : 'Les commandes sont acceptees a tout moment'}
            </CardDescription>
          </CardHeader>
          {form.opening_hours?.enabled && (
            <CardContent>
              <div className="space-y-2">
                {DAYS.map((day) => {
                  const daySchedule = schedule[day.id] || { open: true, start: '10:00', end: '23:00' };
                  return (
                    <div key={day.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${daySchedule.open ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`} data-testid={`schedule-day-${day.id}`}>
                      <Switch
                        checked={daySchedule.open}
                        onCheckedChange={(checked) => updateSchedule(day.id, 'open', checked)}
                        data-testid={`schedule-open-${day.id}`}
                      />
                      <span className="w-24 text-sm font-medium">{day.name}</span>
                      {daySchedule.open ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={daySchedule.start}
                            onChange={(e) => updateSchedule(day.id, 'start', e.target.value)}
                            className="w-32 h-8 text-sm"
                            data-testid={`schedule-start-${day.id}`}
                          />
                          <span className="text-gray-400">-</span>
                          <Input
                            type="time"
                            value={daySchedule.end}
                            onChange={(e) => updateSchedule(day.id, 'end', e.target.value)}
                            className="w-32 h-8 text-sm"
                            data-testid={`schedule-end-${day.id}`}
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Ferme</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>
              HubRise
            </CardTitle>
            <CardDescription>
              Envoyez automatiquement les commandes GlassOrder vers votre caisse connectée via HubRise
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Activer HubRise</p>
                <p className="text-sm text-gray-500">
                  Autorise l’envoi des commandes vers HubRise
                </p>
              </div>
              <Switch
                checked={form.hubrise_enabled}
                onCheckedChange={(checked) => setForm({ ...form, hubrise_enabled: checked })}
              />
            </div>

            {form.hubrise_enabled && (
              <>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">Envoi automatique des commandes</p>
                    <p className="text-sm text-gray-500">
                      Chaque nouvelle commande est poussée vers HubRise
                    </p>
                  </div>
                  <Switch
                    checked={form.hubrise_auto_send_orders}
                    onCheckedChange={(checked) => setForm({ ...form, hubrise_auto_send_orders: checked })}
                  />
                </div>

                <div>
                  <Label htmlFor="hubrise-location-id">HubRise Location ID</Label>
                  <Input
                    id="hubrise-location-id"
                    value={form.hubrise_location_id}
                    onChange={(e) => setForm({ ...form, hubrise_location_id: e.target.value })}
                    className="mt-1"
                    placeholder="ex: 3r4s3-1"
                  />
                </div>

                <div>
                  <Label htmlFor="hubrise-token">HubRise Access Token</Label>
                  <Input
                    id="hubrise-token"
                    type="password"
                    value={form.hubrise_access_token}
                    onChange={(e) => setForm({ ...form, hubrise_access_token: e.target.value })}
                    className="mt-1"
                    placeholder="Access token HubRise"
                  />
                </div>

                <div>
                  <Label htmlFor="hubrise-connection-name">Connection Name (optionnel)</Label>
                  <Input
                    id="hubrise-connection-name"
                    value={form.hubrise_connection_name}
                    onChange={(e) => setForm({ ...form, hubrise_connection_name: e.target.value })}
                    className="mt-1"
                    placeholder="ex: glassorder-main"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Button type="submit" disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </form>
    </div>
  );
}
